import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { SUPPORTED_LANGUAGES } from "@/i18n";
import { stripLocaleFromPath } from "@/i18n/locale-utils";
import { ogLocaleFor } from "@/lib/seo/hreflang";

interface SeoProps {
  title: string;
  description?: string;
  canonical?: string;
  image?: string;
  type?: "website" | "article" | "product";
  /** OG image template variant: catalog, blog, case-study, bootcamp, default */
  ogVariant?: "default" | "catalog" | "blog" | "case-study" | "bootcamp";
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const OG_FN_BASE = `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID}.supabase.co/functions/v1/og-image`;

export function Seo({ title, description, canonical, image, type = "website", ogVariant, jsonLd }: SeoProps) {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "en").slice(0, 2);

  // Force a re-render when the language changes so all <meta>/<title>
  // children reflect the new locale immediately.
  const [, setTick] = useState(0);
  useEffect(() => {
    const cb = () => setTick((t) => t + 1);
    i18n.on("languageChanged", cb);
    return () => i18n.off("languageChanged", cb);
  }, [i18n]);

  const fullTitle = title.length > 60 ? title.slice(0, 57) + "…" : title;
  const desc = description ? (description.length > 160 ? description.slice(0, 157) + "…" : description) : undefined;
  // Canonical: prefer explicit prop. Fallback strips query + hash so UTMs / preview flags
  // never leak into rel=canonical.
  const url =
    canonical ??
    (typeof window !== "undefined"
      ? `${window.location.origin}${window.location.pathname}`
      : undefined);

  // Auto-generate OG image from edge function when ogVariant given and no explicit image.
  const resolvedImage = image ?? (ogVariant
    ? `${OG_FN_BASE}?type=${encodeURIComponent(ogVariant)}&title=${encodeURIComponent(fullTitle)}${desc ? `&subtitle=${encodeURIComponent(desc)}` : ""}`
    : undefined);

  const jsonLdArr = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      {desc && <meta name="description" content={desc} />}
      {url && <link rel="canonical" href={url} />}

      {/* hreflang alternates — URL-based locale prefix per Pass 2F */}
      {url && (() => {
        try {
          const u = new URL(url);
          const { rest } = stripLocaleFromPath(u.pathname);
          const path = rest === "/" ? "" : rest;
          const buildHref = (loc: string) => `${u.origin}/${loc}${path}${u.search}${u.hash}`;
          return (
            <>
              {SUPPORTED_LANGUAGES.map((l) => (
                <link key={l} rel="alternate" hrefLang={l} href={buildHref(l)} />
              ))}
              <link rel="alternate" hrefLang="x-default" href={buildHref("en")} />
            </>
          );
        } catch {
          return null;
        }
      })()}

      <meta property="og:title" content={fullTitle} />
      {desc && <meta property="og:description" content={desc} />}
      <meta property="og:type" content={type} />
      <meta property="og:locale" content={ogLocaleFor(lang)} />
      <meta property="og:site_name" content="Bootpreneurs" />
      {url && <meta property="og:url" content={url} />}
      {resolvedImage && <meta property="og:image" content={resolvedImage} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {desc && <meta name="twitter:description" content={desc} />}
      {resolvedImage && <meta name="twitter:image" content={resolvedImage} />}

      {jsonLdArr.map((node, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(node)}
        </script>
      ))}
    </Helmet>
  );
}
